/**
 * Phase 110 Plan 110-08 (Surface A) — per-project discount configuration
 * card on `ProjectDetailPage`.
 *
 * `canManage` MUST be bound to the admin predicate ALONE at the call site
 * (`isAdmin` — never `isManager || isAdmin`). D-08 reserves discount writes
 * to admins, tighter than `BalicekHeroCard`'s `canManage` prop (bound to
 * `isManager || isAdmin` there). Rendering write controls to a manager here
 * would show a control the server rejects with 403 under `requireAdmin`
 * (Plan 110-04) — this is the one place this card deliberately diverges
 * from its sibling's binding. Keep the prop NAME `canManage` (matches the
 * sibling's interface) but never widen its binding at the call site.
 *
 * Read-only (canManage === false) means every write control is NOT
 * RENDERED — absent from the DOM, never `disabled`. The server's own read
 * gate (`requireAdminOrProjectMemberByProject`) is what lets a project PM
 * reach this data at all; the UI's job is affordance, not enforcement
 * (T-110-70).
 */
import { useEffect, useState } from 'react';
import { Card } from '../ui/Card';
import { Btn } from '../ui/Btn';
import { Modal } from '../ui/Modal';
import { Field } from '../ui/Field';
import { TextInput } from '../ui/TextInput';
import { useToast } from '../ui/Toast';
import { ApiClientError } from '../../services/api/client';
import {
  useProjectDiscountConfig,
  useProjectDiscountTiers,
  useUpsertProjectDiscountConfig,
  useCreateProjectDiscountTier,
  useUpdateProjectDiscountTier,
  useDeleteProjectDiscountTier,
} from '../../hooks/useProjectDiscount';
import type { DiscountMode, ProjectDiscountTier } from '../../services/api/projectDiscount';
import { FIXED_PRICE_PAYMENT_TYPE_ID } from '../../constants/typeIds';

export interface DiscountConfigCardProps {
  projectId: number;
  /** From `Project.paymentTypeId`; drives D-01 (Fixed Price has no Tiers mode). */
  paymentTypeId: number | null;
  /** Constant from `frontend/src/constants/typeIds.ts` (per checker I-13). */
  slaBalicekPaymentTypeId: number;
  /** MUST be `isAdmin` alone at the call site — see file docstring (D-08). */
  canManage: boolean;
}

const MODES: { value: DiscountMode; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'tiers', label: 'Tiers' },
  { value: 'manual', label: 'Manual' },
];

const STALE_TAB_MESSAGE = 'You no longer have permission to change this. Refresh the page.';
const PERCENTAGE_RANGE_MESSAGE = 'Percentage must be a whole number from 0 to 100.';
const MIN_HOURS_RANGE_MESSAGE = 'Min hours must be a whole number at or above 0.';
const MODE_SWITCH_AWAY_MESSAGE =
  'Switch to no discount? Existing tier rows are kept but stop applying to new invoices.';
const GENERIC_SAVE_ERROR = 'Could not save. Please try again.';
const RETAINER_MOOT_STATEMENT =
  'All billed hours count toward the tier (no package retainer exists on this project)';

function parseWholeNumber(value: string, min: number, max: number): number | null {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n < min || n > max) return null;
  return n;
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiClientError) {
    if (err.statusCode === 403) return STALE_TAB_MESSAGE;
    return err.message || GENERIC_SAVE_ERROR;
  }
  return GENERIC_SAVE_ERROR;
}

interface TierFormState {
  minHours: string;
  percentage: string;
}

function emptyTierForm(): TierFormState {
  return { minHours: '', percentage: '' };
}

export function DiscountConfigCard({
  projectId,
  paymentTypeId,
  slaBalicekPaymentTypeId,
  canManage,
}: DiscountConfigCardProps) {
  const toast = useToast();

  const configQuery = useProjectDiscountConfig(projectId);
  const tiersQuery = useProjectDiscountTiers(projectId);
  const upsertConfig = useUpsertProjectDiscountConfig();
  const createTier = useCreateProjectDiscountTier();
  const updateTier = useUpdateProjectDiscountTier();
  const deleteTier = useDeleteProjectDiscountTier();

  const config = configQuery.data?.data;
  const mode: DiscountMode = config?.mode ?? 'none';
  const tiers = (tiersQuery.data?.data ?? []).slice().sort((a, b) => a.minHours - b.minHours);

  const tiersDisabled = paymentTypeId === FIXED_PRICE_PAYMENT_TYPE_ID;
  const isBalicek = paymentTypeId === slaBalicekPaymentTypeId;
  const defaultConfigureMode: DiscountMode = tiersDisabled ? 'manual' : 'tiers';

  // ---- Mode switch (D-06) — structurally exclusive, confirmed when leaving
  // a configured mode so an admin never thinks their data was deleted.
  const [pendingMode, setPendingMode] = useState<DiscountMode | null>(null);

  const applyModeChange = (nextMode: DiscountMode) => {
    upsertConfig.mutate(
      { projectId, input: { mode: nextMode } },
      { onError: (err) => toast(errorMessage(err), 'error') },
    );
  };

  const selectMode = (nextMode: DiscountMode) => {
    if (nextMode === mode) return;
    if (mode === 'tiers' || mode === 'manual') {
      setPendingMode(nextMode);
      return;
    }
    applyModeChange(nextMode);
  };

  const confirmModeSwitch = () => {
    if (pendingMode) applyModeChange(pendingMode);
    setPendingMode(null);
  };

  // ---- Manual percentage ----
  const [manualDraft, setManualDraft] = useState('');
  const [manualError, setManualError] = useState<string | null>(null);
  useEffect(() => {
    setManualDraft(config?.manualPercentage != null ? String(config.manualPercentage) : '');
  }, [config?.manualPercentage]);

  const saveManualPercentage = () => {
    const parsed = parseWholeNumber(manualDraft, 0, 100);
    if (parsed === null) {
      setManualError(PERCENTAGE_RANGE_MESSAGE);
      return;
    }
    setManualError(null);
    upsertConfig.mutate(
      { projectId, input: { manualPercentage: parsed } },
      {
        onSuccess: () => toast('Discount configuration saved', 'success'),
        onError: (err) => setManualError(errorMessage(err)),
      },
    );
  };

  // ---- Retainer-in-base switch (D-02/D-04) — only meaningful on the SLA
  // hour-package payment type ----
  const toggleRetainer = () => {
    upsertConfig.mutate(
      { projectId, input: { retainerInBase: !(config?.retainerInBase ?? false) } },
      { onError: (err) => toast(errorMessage(err), 'error') },
    );
  };

  // ---- Tier add/edit modal ----
  const [tierModalOpen, setTierModalOpen] = useState(false);
  const [editingTier, setEditingTier] = useState<ProjectDiscountTier | null>(null);
  const [tierForm, setTierForm] = useState<TierFormState>(emptyTierForm());
  const [tierFormError, setTierFormError] = useState<string | null>(null);

  const openAddTier = () => {
    setEditingTier(null);
    setTierForm(emptyTierForm());
    setTierFormError(null);
    setTierModalOpen(true);
  };

  const openEditTier = (tier: ProjectDiscountTier) => {
    setEditingTier(tier);
    setTierForm({ minHours: String(tier.minHours), percentage: String(tier.percentage) });
    setTierFormError(null);
    setTierModalOpen(true);
  };

  const submitTier = () => {
    const minHours = parseWholeNumber(tierForm.minHours, 0, Number.MAX_SAFE_INTEGER);
    if (minHours === null) {
      setTierFormError(MIN_HOURS_RANGE_MESSAGE);
      return;
    }
    const percentage = parseWholeNumber(tierForm.percentage, 0, 100);
    if (percentage === null) {
      setTierFormError(PERCENTAGE_RANGE_MESSAGE);
      return;
    }
    setTierFormError(null);
    const input = { minHours, percentage };
    const onSuccess = () => {
      setTierModalOpen(false);
      toast('Tier saved', 'success');
    };
    const onError = (err: unknown) => setTierFormError(errorMessage(err));
    if (editingTier) {
      updateTier.mutate({ projectId, tierId: editingTier.id, input }, { onSuccess, onError });
    } else {
      createTier.mutate({ projectId, input }, { onSuccess, onError });
    }
  };

  // ---- Tier delete confirmation — always the shared Modal primitive,
  // never a native browser dialog (checker item I-19 / T-41-36) ----
  const [tierToDelete, setTierToDelete] = useState<ProjectDiscountTier | null>(null);

  const confirmDeleteTier = () => {
    if (!tierToDelete) return;
    deleteTier.mutate(
      { projectId, tierId: tierToDelete.id },
      {
        onSuccess: () => {
          toast('Tier deleted', 'success');
          setTierToDelete(null);
        },
        onError: (err) => {
          toast(errorMessage(err), 'error');
          setTierToDelete(null);
        },
      },
    );
  };

  if (configQuery.isLoading || tiersQuery.isLoading) {
    return (
      <Card title="Discount">
        <div className="py-6 text-center text-ms-text-muted">Loading…</div>
      </Card>
    );
  }

  // MKS-206 (110-REVIEW IN-01) — no mutation-triggering control stays clickable
  // while its request is in flight. A double-click used to fire the same
  // create/update twice, which is precisely how WR-01's duplicate-threshold
  // race was reachable from this very card.
  const tierWritePending = createTier.isPending || updateTier.isPending || deleteTier.isPending;
  const anyWritePending = tierWritePending || upsertConfig.isPending;

  const cardAction =
    canManage && mode === 'tiers' ? (
      <Btn size="sm" variant="accent" onClick={openAddTier} disabled={anyWritePending}>
        + Add tier
      </Btn>
    ) : undefined;

  return (
    <Card title="Discount" action={cardAction}>
      {canManage ? (
        <div className="space-y-4">
          <div className="flex gap-1">
            {MODES.map((m) => {
              const fixedPriceBlocked = m.value === 'tiers' && tiersDisabled;
              // IN-01 — a mode button writes the config too, so it is subject to
              // the same in-flight gate as the tier controls.
              const disabled = fixedPriceBlocked || anyWritePending;
              const selected = m.value === mode;
              return (
                <button
                  key={m.value}
                  type="button"
                  disabled={disabled}
                  title={
                    fixedPriceBlocked
                      ? 'Fixed Price has no billed hours to key a tier on'
                      : undefined
                  }
                  onClick={() => selectMode(m.value)}
                  className={`px-3 py-1.5 rounded-md text-[12px] font-medium border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                    selected
                      ? 'bg-ms-orange-dark text-white border-ms-orange-dark'
                      : 'bg-white text-ms-text-secondary border-ms-border hover:bg-ms-surface-2'
                  }`}
                >
                  {m.label}
                </button>
              );
            })}
          </div>

          {mode === 'none' && (
            <div>
              <p className="text-[13px] font-semibold text-ms-text-strong mb-1">
                No discount configured
              </p>
              <p className="text-[12px] text-ms-text-secondary">
                This project invoices at full price.{' '}
                <button
                  type="button"
                  onClick={() => selectMode(defaultConfigureMode)}
                  className="text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Configure discount
                </button>
              </p>
            </div>
          )}

          {mode === 'tiers' && (
            <div className="space-y-3">
              {tiers.length === 0 ? (
                <p className="text-[12px] text-ms-text-secondary italic">
                  No tiers yet — every invoice resolves to 0% until at least one tier is added.
                </p>
              ) : (
                <table className="min-w-full text-[12.5px]">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider text-ms-text-muted">
                      <th className="py-2 pr-2 font-semibold">Min hours</th>
                      <th className="py-2 pr-2 font-semibold">Percentage</th>
                      <th className="py-2 pr-2 font-semibold"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ms-border-subtle">
                    {tiers.map((t) => (
                      <tr key={t.id}>
                        <td className="py-2 pr-2 tabular-nums">{t.minHours}</td>
                        <td className="py-2 pr-2 tabular-nums">{t.percentage}%</td>
                        <td className="py-2 pr-2">
                          <div className="flex gap-1">
                            <Btn
                              size="sm"
                              variant="ghost"
                              onClick={() => openEditTier(t)}
                              disabled={anyWritePending}
                            >
                              Edit
                            </Btn>
                            <Btn
                              size="sm"
                              variant="ghost"
                              onClick={() => setTierToDelete(t)}
                              disabled={anyWritePending}
                            >
                              Delete
                            </Btn>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {isBalicek ? (
                <div className="flex items-start gap-2 pt-2 border-t border-ms-border-subtle">
                  <input
                    type="checkbox"
                    id="discount-retainer-toggle"
                    checked={config?.retainerInBase ?? false}
                    onChange={toggleRetainer}
                    className="mt-0.5 rounded border-ms-border text-ms-orange-dark focus:ring-ms-orange/40"
                  />
                  <label htmlFor="discount-retainer-toggle" className="text-[12px]">
                    <span className="block font-medium text-ms-text-strong">
                      Include the package retainer in the discountable base
                    </span>
                    <span className="block text-[11px] text-ms-text-muted">
                      Off by default — only overage hours count toward the tier and are
                      discounted.
                    </span>
                  </label>
                </div>
              ) : (
                <div className="flex items-start gap-2 pt-2 border-t border-ms-border-subtle">
                  <input
                    type="checkbox"
                    disabled
                    checked={false}
                    className="mt-0.5 rounded border-ms-border opacity-50"
                  />
                  <span className="text-[12px] text-ms-text-muted">
                    {RETAINER_MOOT_STATEMENT}
                  </span>
                </div>
              )}
            </div>
          )}

          {mode === 'manual' && (
            <div className="space-y-2">
              <Field label="Manual discount percentage" error={manualError ?? undefined}>
                <TextInput
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  value={manualDraft}
                  onChange={(e) => setManualDraft(e.target.value)}
                />
              </Field>
              <div className="flex justify-end">
                <Btn
                  size="sm"
                  variant="accent"
                  onClick={saveManualPercentage}
                  isLoading={upsertConfig.isPending}
                >
                  Save
                </Btn>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {mode === 'none' && (
            <div>
              <p className="text-[13px] font-semibold text-ms-text-strong mb-1">
                No discount configured
              </p>
              <p className="text-[12px] text-ms-text-secondary">
                This project invoices at full price. Only an admin can configure a discount.
              </p>
            </div>
          )}

          {mode === 'tiers' && (
            <div className="space-y-2">
              {tiers.length === 0 ? (
                <p className="text-[12px] text-ms-text-secondary italic">
                  No discount tiers configured yet.
                </p>
              ) : (
                <table className="min-w-full text-[12.5px]">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider text-ms-text-muted">
                      <th className="py-2 pr-2 font-semibold">Min hours</th>
                      <th className="py-2 pr-2 font-semibold">Percentage</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ms-border-subtle">
                    {tiers.map((t) => (
                      <tr key={t.id}>
                        <td className="py-2 pr-2 tabular-nums">{t.minHours}</td>
                        <td className="py-2 pr-2 tabular-nums">{t.percentage}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {isBalicek && (
                <p className="text-[12px] text-ms-text-secondary">
                  Package retainer: {config?.retainerInBase ? 'included' : 'excluded'}
                </p>
              )}
            </div>
          )}

          {mode === 'manual' && (
            <p className="text-[12px] text-ms-text-secondary">
              Manual discount: {config?.manualPercentage ?? 0}%
            </p>
          )}
        </div>
      )}

      <Modal
        open={pendingMode !== null}
        onClose={() => setPendingMode(null)}
        title="Switch discount mode"
        footer={
          <div className="flex justify-end gap-2">
            <Btn
              variant="ghost"
              onClick={() => setPendingMode(null)}
              disabled={upsertConfig.isPending}
            >
              Cancel
            </Btn>
            <Btn variant="accent" onClick={confirmModeSwitch} isLoading={upsertConfig.isPending}>
              Switch
            </Btn>
          </div>
        }
      >
        <p className="text-[12.5px] text-ms-text-secondary">{MODE_SWITCH_AWAY_MESSAGE}</p>
      </Modal>

      <Modal
        open={tierToDelete !== null}
        onClose={() => setTierToDelete(null)}
        title="Delete tier"
        footer={
          <div className="flex justify-end gap-2">
            <Btn
              variant="ghost"
              onClick={() => setTierToDelete(null)}
              disabled={deleteTier.isPending}
            >
              Cancel
            </Btn>
            <Btn variant="danger" onClick={confirmDeleteTier} isLoading={deleteTier.isPending}>
              Delete
            </Btn>
          </div>
        }
      >
        <p className="text-[12.5px] text-ms-text-secondary">
          Delete the {tierToDelete?.minHours}+ h tier ({tierToDelete?.percentage}%)? Invoices
          generated after this change use the remaining tiers.
        </p>
      </Modal>

      <Modal
        open={tierModalOpen}
        onClose={() => setTierModalOpen(false)}
        title={editingTier ? 'Edit tier' : 'Add tier'}
        footer={
          <div className="flex justify-end gap-2">
            <Btn
              variant="ghost"
              onClick={() => setTierModalOpen(false)}
              disabled={tierWritePending}
            >
              Cancel
            </Btn>
            <Btn variant="accent" onClick={submitTier} isLoading={tierWritePending}>
              Save
            </Btn>
          </div>
        }
      >
        <div className="space-y-3">
          <Field label="Min hours">
            <TextInput
              type="number"
              min={0}
              step={1}
              value={tierForm.minHours}
              onChange={(e) => setTierForm({ ...tierForm, minHours: e.target.value })}
            />
          </Field>
          <Field label="Percentage">
            <TextInput
              type="number"
              min={0}
              max={100}
              step={1}
              value={tierForm.percentage}
              onChange={(e) => setTierForm({ ...tierForm, percentage: e.target.value })}
            />
          </Field>
          {tierFormError && <p className="text-[11px] text-rose-600">{tierFormError}</p>}
        </div>
      </Modal>
    </Card>
  );
}
